import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import './ServiceCombobox.scss';

export interface ServiceComboboxOption {
  value: string;
  label: string;
}

interface ServiceComboboxProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ServiceComboboxOption[];
  placeholder?: string;
  emptyHint?: string;
  error?: string;
}

export default function ServiceCombobox({
  label, value, onChange, options, placeholder, emptyHint, error,
}: ServiceComboboxProps) {
  const autoId = useId();
  const inputId = `combo-${autoId}`;
  const listId = `${inputId}-listbox`;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const filtered = useMemo(() => {
    const q = value.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q));
  }, [options, value]);

  useEffect(() => { setActive(0); }, [value, open]);

  const close = useCallback(() => setOpen(false), []);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, close]);

  useEffect(() => {
    if (open && listRef.current) {
      const el = listRef.current.querySelector<HTMLLIElement>(`[data-idx="${active}"]`);
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [open, active]);

  const choose = (i: number) => {
    const opt = filtered[i];
    if (!opt) return;
    onChange(opt.label);
    setOpen(false);
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive(i => Math.min(filtered.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setOpen(true); setActive(i => Math.max(0, i - 1)); }
    else if (e.key === 'Enter') {
      if (open && filtered[active]) { e.preventDefault(); choose(active); }
    }
    else if (e.key === 'Escape') { if (open) { e.preventDefault(); setOpen(false); } }
  };

  const showEmpty = open && filtered.length === 0 && value.trim().length > 0;

  return (
    <div ref={rootRef} className={['combo', error ? 'combo--error' : ''].filter(Boolean).join(' ')}>
      <label className="combo__label" htmlFor={inputId}>{label}</label>
      <div className="combo__wrap">
        <input
          id={inputId}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listId}
          aria-invalid={!!error || undefined}
          className="combo__input"
          value={value}
          placeholder={placeholder}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          autoComplete="off"
        />
        <button
          type="button"
          className="combo__chev"
          tabIndex={-1}
          aria-label="toggle"
          onClick={() => setOpen(o => !o)}
        >
          <ChevronDown size={16} />
        </button>
        {open && filtered.length > 0 ? (
          <ul ref={listRef} id={listId} role="listbox" className="combo__list">
            {filtered.map((opt, i) => (
              <li
                key={opt.value}
                data-idx={i}
                role="option"
                aria-selected={i === active}
                className={[
                  'combo__option',
                  i === active ? 'combo__option--active' : '',
                ].filter(Boolean).join(' ')}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => { e.preventDefault(); choose(i); }}
              >
                {opt.label}
              </li>
            ))}
          </ul>
        ) : null}
        {showEmpty && emptyHint ? (
          <div className="combo__empty">{emptyHint}</div>
        ) : null}
      </div>
      {error ? <span className="combo__error" role="alert">{error}</span> : null}
    </div>
  );
}
